import './style.scss';

import { DownloadOutlined } from '@ant-design/icons';
import { PageTitle } from '@components/common/PageTitle/PageTitle';
import { PagingType, UTable } from '@components/common/UTable/UTable';
import { useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';

import { useGetOrganizationsShortQuery } from '@/api/organizations/api';
import {
  useLazyDownloadControlReportQuery,
  useLazyDownloadTaskArchiveReportQuery,
} from '@/api/report/api';
import { useGetTasksArchiveQuery } from '@/api/vaccination/task/api';
import { CONTROL } from '@/constants/routes';
import { VaccinationArchiveRecord } from '@/pages/Vaccination/components/task/types';
import { TFilters } from '@/shared/api/types';
import { useAppNotification } from '@/shared/hooks/useAppNotification';
import { OrganizationLevelType } from '@/shared/organizations/enums';
import { vaccination } from '@/shared/security/permissions/vaccination';
import { downloadBlob } from '@/utils/utils';

import { columns } from './columns.config';
import { filterSchema } from './filters.config';

export const VaccinationArchive = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { showWarning } = useAppNotification();
  const { pathname } = useLocation();
  const orgId = searchParams.get('organizationId') || '';
  const [filters, setFilters] = useState<TFilters>(
    orgId ? { organizationId: orgId } : {},
  );
  const [paging, setPaging] = useState<PagingType>({ page: 0, size: 10 });

  const { data, isFetching } = useGetTasksArchiveQuery({
    filters,
    paging,
    sort: [{ key: 'taskDate', order: 'desc' }],
  });

  const { data: organizations } = useGetOrganizationsShortQuery({
    level: OrganizationLevelType.ORGANIZATION,
  });
  const [downloadTaskArchiveReport, { isLoading: isTaskDownloadLoading }] =
    useLazyDownloadTaskArchiveReportQuery();

  const [downloadControlArchiveReport, { isLoading: isControlDownloadLoading }] =
    useLazyDownloadControlReportQuery();

  const handleDownloadReport = useCallback(
    async (record: VaccinationArchiveRecord) => {
      let result;
      const isControlArchive = pathname.includes(CONTROL);

      const { organizationId, taskDate, status } = record;
      const filters = {
        filters: isControlArchive
          ? { organizationId, taskDate }
          : { organizationId, taskDate, status },
      };

      if (isControlArchive) {
        result = await downloadControlArchiveReport(filters, false).unwrap();
      } else {
        result = await downloadTaskArchiveReport(filters, false).unwrap();
      }

      if (result.meta.response?.status === 204) {
        showWarning('Внимание!', 'Отсутствуют данные для формирования файла');
        return;
      }

      const filename = `${record.organization.name || ''}_${taskDate}.pdf`;
      downloadBlob(result.data, filename);
    },
    [pathname],
  );

  const tableData = useMemo(() => {
    return data?.content?.map((item, index) => ({
      ...item,
      index: index + 1 + (paging ? paging.page * paging.size : 0),
      organizationName: item.organization.name,
    }));
  }, [data]);

  return (
    <div className="vaccination-archive">
      <PageTitle title={'Архив'} />
      <UTable
        actionColumnWidth={50}
        size={'small'}
        data={tableData}
        columns={columns}
        loading={isFetching}
        paging={paging}
        total={data?.totalElements}
        onPagingChange={setPaging}
        buttons={[
          {
            text: 'Назад',
            action: () => navigate(-1),
          },
        ]}
        filter={{
          filters,
          schema: filterSchema(organizations),
          onFilter: (filters) => setFilters(filters),
          onReset: () => setFilters({}),
          security: { permissions: vaccination.task.filterArchiveTable },
        }}
        actions={(record: VaccinationArchiveRecord) => [
          {
            icon: <DownloadOutlined />,
            action: () => handleDownloadReport(record),
            disabled: isTaskDownloadLoading || isControlDownloadLoading,
            security: { permissions: vaccination.task.exportArchive },
          },
        ]}
      />
    </div>
  );
};
